/**
 * Painel Agora (docs/design): a cliente de agora, embaixo e ao alcance do polegar, com
 * WhatsApp, "+15 min" e "Concluir e cobrar". Sem ninguém agora, mostra a próxima.
 */
import { durationMinOf, formatHourOf, formatMoneyShort, toMs } from '@lindeza/core';
import { type AgendaItem, useRuntime } from '@lindeza/core/hooks';
import { toast, toastError } from '../../app/ui';
import { messageFor, openWhatsapp, useMessageCtx } from '../../app/useMessages';
import { IconCheck, IconClockPlus, IconWhatsapp } from '../../components/Icons';

function minutesLabel(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

export function AgoraPanel({
  item,
  after,
  now,
  timezone,
  onCharge,
  onOpen,
}: {
  /** a de agora (ou a próxima, quando não há ninguém agora) */
  item: AgendaItem;
  /** a que vem depois dela */
  after: AgendaItem | null;
  now: number;
  timezone: string;
  onCharge: () => void;
  onOpen: () => void;
}) {
  const rt = useRuntime();
  const ctx = useMessageCtx();
  const { appointment: a, client, service } = item;
  const start = toMs(a.start_utc);
  const end = toMs(a.end_utc);
  const isNow = start <= now && now < end;
  const left = Math.max(0, Math.round((end - now) / 60_000));
  const until = Math.max(0, Math.round((start - now) / 60_000));
  const pct = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
  const first = (name?: string) => name?.split(' ')[0] ?? 'cliente';

  async function extend() {
    try {
      const r = await rt.actions.rescheduleAppointment(a.id, a.start_utc, { durationMin: durationMinOf(a) + 15 });
      const clash = after && toMs(after.appointment.start_utc) < toMs(r.appointment.end_utc) ? after : null;
      toast(`${first(client?.name)} vai até ${formatHourOf(r.appointment.end_utc, timezone)}.`, {
        action:
          clash && ctx
            ? {
                label: `Avisar ${first(clash.client?.name)}`,
                run: () => openWhatsapp(clash.client?.whatsapp, messageFor(ctx, 'delay', clash.appointment, clash.client, clash.service, { minutos: '15' })),
              }
            : undefined,
        duration: 7000,
      });
    } catch (e) {
      toastError(e);
    }
  }

  return (
    <section className="ag-panel" aria-label={isNow ? 'Agora' : 'Próxima'} data-testid="agora">
      <div className="ag-panel-top">
        <span>{isNow ? `Agora, até ${formatHourOf(a.end_utc, timezone)}` : `Próxima, às ${formatHourOf(a.start_utc, timezone)}`}</span>
        {a.status === 'confirmed' && (
          <span className="ag-panel-ok">
            <IconCheck /> Confirmada
          </span>
        )}
        {isNow && <b className="ag-panel-left">faltam {minutesLabel(left)}</b>}
        {!isNow && a.status !== 'confirmed' && <b>daqui a {minutesLabel(until)}</b>}
      </div>
      <button className="ag-panel-name" onClick={onOpen} aria-label={`${client?.name ?? 'Cliente'}: abrir atendimento`}>
        {client?.name ?? 'Cliente'}
      </button>
      <div className="ag-panel-svc">
        <span>{service?.name ?? 'Serviço'}</span>
        <b>{formatMoneyShort(Math.max(0, a.price_cents - (a.discount_cents ?? 0)))}</b>
      </div>
      {isNow && (
        <div className="ag-prog">
          <div className="ag-prog-big">
            <b>faltam {minutesLabel(left)}</b>
            <span>começou às {formatHourOf(a.start_utc, timezone)}</span>
          </div>
          <div className="ag-bar" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} aria-label="Andamento do atendimento">
            <i style={{ width: `${pct}%` }} />
          </div>
          <b>faltam {minutesLabel(left)}</b>
          <div className="ag-legend">
            <span>{formatHourOf(a.start_utc, timezone)}</span>
            <span>{formatHourOf(a.end_utc, timezone)}</span>
          </div>
        </div>
      )}
      {after && (
        <div className="ag-panel-next">
          <span>{isNow ? 'Depois dela' : 'Depois'}</span>
          <b>
            {after.client?.name ?? 'Cliente'}, {formatHourOf(after.appointment.start_utc, timezone)}, {after.service?.name.toLowerCase()}
          </b>
        </div>
      )}
      <div className="ag-pair">
        <button
          className="ag-ghost"
          disabled={!ctx}
          onClick={() => ctx && openWhatsapp(client?.whatsapp, messageFor(ctx, 'auto', a, client, service))}
          aria-label={`WhatsApp para ${client?.name ?? 'cliente'}`}
        >
          <span className="wa-dot">
            <IconWhatsapp />
          </span>
          WhatsApp
        </button>
        {isNow ? (
          <button className="ag-ghost" onClick={() => void extend()} aria-label="Atrasou? Mais 15 minutos">
            <IconClockPlus />
            <span className="short">+15 min</span>
            <span className="long">Atrasou? +15 min</span>
          </button>
        ) : (
          <button className="ag-ghost" onClick={onOpen}>
            Ver atendimento
          </button>
        )}
      </div>
      {isNow && (
        <button className="ag-main" onClick={onCharge}>
          <IconCheck strokeWidth={2.75} /> Concluir e cobrar
        </button>
      )}
    </section>
  );
}
